import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router';
import cartImg from '@/assets/images/cart_logo.png';
import smallLogo from '@/assets/images/logo.png';
import updateImg from '@/assets/images/system-Update_Refresh-Browser_Graphic.png';
import Menu from '@/components/Menu/Menu';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import infoMsgStyles from '@/components/UI/InfoMsg/InfoMsg.module.scss';
import UserSettingsMenu from '@/components/UserSettingsMenu/UserSettingsMenu';
import { env, ENV, CART_QMB_PLANT_APPROVER, CART_PLANT_SUPERVISOR, REPORTER, RD_COMMITTEE_COORDINATOR, ROUTES, STORAGE, getAbsoluteRoute, BASE_PATH, QMB_PLANT_APPROVER_JV }
  from '@/helpers/constants';
import { useClearCacheCtx } from '@/hooks/ClearCache';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useSave from '@/hooks/useSave';
import { getPlantName } from '@/redux/Header/header.reducer';
import Notifications from './Notifications/Notifications';
import styles from './Header.module.scss';

interface HeaderProps {
  plantName: string;
  setToggleMenu: (toggleMenu: (fromAdminTile: boolean) => void) => void;
}

const Header = (props: HeaderProps) => {
  const { plantName, setToggleMenu } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const authService = useAuth();
  const saveChanges = useSave(localStorage.getItem(STORAGE.PREVIOUS_TAB));
  const [parsedTitle, setParsedTitle] = useState('');
  const location = useLocation().pathname;
  const environment = env !== ENV.PRO ? env.toUpperCase().substr(0, 3) : '';
  const notificationsVisible = !authService.checkRoles([REPORTER]) && authService.isLogged;
  const isDeniedPage = location === getAbsoluteRoute(ROUTES.DENIED_PAGE);
  const isLogin = location === getAbsoluteRoute(ROUTES.LOGIN);
  const isDashboard = location === getAbsoluteRoute(ROUTES.DASHBOARD);
  const isSubCROverview = location.startsWith(getAbsoluteRoute(ROUTES.SUB_CR_OVERVIEW));
  const isCROverview = location.startsWith(getAbsoluteRoute(ROUTES.CR_OVERVIEW));
  const isFiles = location.includes(`${getAbsoluteRoute(ROUTES.FILES)}/`) || location.includes(getAbsoluteRoute(ROUTES.FILES_LIST));
  const isDetail = location.includes(`${getAbsoluteRoute(ROUTES.CR_DETAIL)}/`) || location.includes(`${getAbsoluteRoute(ROUTES.SUB_CR_DETAIL)}/`);
  const dark = isFiles || isDeniedPage || isLogin;
  const isMultiRoleUser = authService.checkMultiRoleUser();
  const { isLatestVersion, emptyCacheStorage } = useClearCacheCtx();
  const [clearCache, setClearCache] = useState(false);

  const confirmClearCache = () => {
    setClearCache(false);
  };

  const clearCacheAction = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setClearCache(false);
    await emptyCacheStorage();
    window.location.reload();
  };

  const clearBrowserCache = () => {
    if (!isLatestVersion) {
      setClearCache(true);
    }
  };

  useEffect(() => {
    if (authService.isLogged) {
      clearBrowserCache();
    }
  }, [authService.isLogged]); // eslint-disable-line react-hooks/exhaustive-deps

  const getParsedTitle = () => {
    if (authService.checkRoles([CART_QMB_PLANT_APPROVER, QMB_PLANT_APPROVER_JV, CART_PLANT_SUPERVISOR]) && isSubCROverview) {
      return t('titles.sub-campaign-overview-plant', { plant: plantName });
    }

    if (isCROverview && authService.checkRole(RD_COMMITTEE_COORDINATOR)) {
      return t('titles.campaign-overview-forX', { x: 'RD & MP' });
    }

    const pathWithoutBase = location.startsWith(BASE_PATH) ? location.replace(BASE_PATH, '') : location;
    const title = pathWithoutBase.toLowerCase().split('/').filter(Boolean)[0] || 'dashboard';

    return t(`titles.${title}`);
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isDetail) {
      saveChanges();
    }

    if (isDashboard) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    setParsedTitle(getParsedTitle());
  }, [plantName, location, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (dark) {
    return (
      <>
        <div className={`${styles.Header} ${styles.Dark}`} data-testid="cart-menu-dark">
          <div className={styles.Logo}>
            <img id="logo" src={cartImg} alt="Cart Logo" />
            <span>CaRT {environment}</span>
          </div>
          <div className={`${isFiles ? '' : styles.Help}`}>
            {isLogin
              ? <span> ? </span>
              : <div className={styles.Settings}><UserSettingsMenu isFiles={isFiles} dark /></div>}
          </div>
        </div>
        {(isMultiRoleUser && !isLogin) && (<InfoMsg customClass={infoMsgStyles.MultiRoleInfoMsg} msg={t('header.multiRoleInfoMsg')} />)}
      </>
    );
  }

  return (
    <>
      <div className={styles.Header} data-testid="cart-menu">
        <div className={styles.Logo}>
          <Menu setToggleMenu={setToggleMenu} />
          <Link
            to={getAbsoluteRoute(ROUTES.DASHBOARD)}
            className={styles.Link}
            onClick={handleClick}
          >
            <img id="logo" src={smallLogo} alt="Cart Logo" />
            <span className={styles.TextLogo}>CaRT {environment}</span>
          </Link>
        </div>
        <div className={styles.HeaderTitle}>{parsedTitle}</div>
        <div className={styles.Wrapper}>
          <UserSettingsMenu isFiles={false} dark={false} />
          {notificationsVisible && <Notifications />}
        </div>
      </div>
      {isMultiRoleUser && (<InfoMsg customClass={infoMsgStyles.MultiRoleInfoMsg} msg={t('header.multiRoleInfoMsg')} />)}
      {clearCache && (
        <ConfirmDialog
          close={confirmClearCache}
          title={t('dialog.newVersionTitle')}
          confirm={clearCacheAction}
          message={t('dialog.newVersion')}
          confirmBtnText={t('dialog.clearCache')}
          closeBtnText={t('dialog.close')}
        >
          <div className="contentImg">
            <img id="logo" src={updateImg} alt="Empty task" />
          </div>
        </ConfirmDialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  plantName: getPlantName(state),
});

export default React.memo(connect(mapStateToProps)(Header), (prev, next) => JSON.stringify(prev) === JSON.stringify(next));
